export interface PatientOwnerOption {
  id: string;
  name: string;
  role?: string;
  currentOnly?: boolean;
}

interface PatientOwnerStaff {
  id: string;
  name: string;
  role?: string | null;
  status: string;
  is_doctor?: boolean | null;
}

interface CurrentPatientOwner {
  assignedOwnerStaffId: string | null;
  assignedOwnerName: string | null;
}

interface ActiveDoctorRow {
  name: string;
  status: string;
}

// Match the existing Plato owner-mapping convention. The scheduler's active
// doctor roster is authoritative; its staff identity may be inactive to keep
// doctors out of the nursing rota. Keep the staff ID required by patient cases.
const doctorNameKey = (name: string) =>
  name.trim().toUpperCase().replace(/^DR\.?\s+/, '').replace(/[^A-Z]/g, '');

export const getPatientDoctorOwners = (
  staff: readonly PatientOwnerStaff[],
  doctorRoster: readonly ActiveDoctorRow[] = [],
): PatientOwnerOption[] => {
  const activeNames = new Set(doctorRoster.filter((doctor) => doctor.status === 'active').map((doctor) => doctorNameKey(doctor.name)));
  const staffNameCounts = new Map<string, number>();
  for (const member of staff) {
    const key = doctorNameKey(member.name);
    staffNameCounts.set(key, (staffNameCounts.get(key) ?? 0) + 1);
  }
  return staff.filter((member) => {
    const isDoctor = member.is_doctor === true ||
      (/\b(doctor|endodontist|dentist)\b/i.test(member.role ?? '') && !/\bassistant\b/i.test(member.role ?? ''));
    const key = doctorNameKey(member.name);
    const activeDoctorIdentity = key && activeNames.has(key) && staffNameCounts.get(key) === 1;
    return isDoctor && (member.status === 'active' || activeDoctorIdentity);
  }).map((member) => ({ id: member.id, name: member.name, role: member.role ?? undefined }));
};

export const getPatientOwnerChoices = (
  doctors: readonly PatientOwnerOption[],
  current: CurrentPatientOwner | null | undefined,
): PatientOwnerOption[] => {
  if (!current?.assignedOwnerStaffId || doctors.some((owner) => owner.id === current.assignedOwnerStaffId)) {
    return [...doctors];
  }

  return [...doctors, {
    id: current.assignedOwnerStaffId,
    name: current.assignedOwnerName ?? 'Current owner',
    currentOnly: true,
  }];
};
